import PropTypes from 'prop-types';
import { useState } from 'react';
import Helmet from '../../components/Helmet';
import { RWebShare } from 'react-web-share';

import { Button, Icons, parseToken, windowExists } from '@classity-edtech/ui-base';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { CircularProgress, Container, Stack, Typography } from '@mui/material';
import { useGetPageContentQuery } from '../../services/seoContent';
import { useVerifyEmailLinkQuery } from '../../services/authentication';
import { useGetUserQuery } from '../../services/profile';
import { webPaths } from '../../routes/paths';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { CheckCircle } from '@mui/icons-material';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { ArrowDownIcon, FullLogo } from '@classity-edtech/ui-base/dist/components/Displays';
import { useRelatedUsersQuery } from '../../services/users';
import { FormattedMessage } from 'react-intl';

export interface EmailConfirmationProps extends MuiBoxProps {}

export const EmailConfirmation = ({ className }: EmailConfirmationProps) => {
  const screens = useAppMediaQuery();

  const navigate = useNavigate();
  const { token } = useParams();
  const tokenUser =
    windowExists &&
    parseToken(
      (windowExists?.localStorage as { getItem: (key: string) => void })?.getItem('token'),
    );
  const [showMore, setShowMore] = useState(false);
  const { data, isLoading: fetchingContent } = useGetPageContentQuery({
    tag: 'emailVerification',
    contentType: 'page',
  });
  const { data: shareData, isLoading: fetchingShareData } = useGetPageContentQuery({
    tag: 'referFriend',
    contentType: 'page',
  });
  const { data: user } = useGetUserQuery({ userId: tokenUser.user }, { skip: !tokenUser.user });
  const { data: relatedUsers } = useRelatedUsersQuery({
    userId: tokenUser.user,
  });

  const { isError } = useVerifyEmailLinkQuery(
    {
      link: token,
    },
    { skip: !token },
  );

  const extractData = () => {
    const title = shareData?.sections?.[0]?.fields?.list?.[0]?.fields?.title;
    const text =
      shareData?.sections?.[0]?.fields?.list?.[0]?.fields?.description?.content?.[0]?.content?.[0]
        ?.value;
    const url = shareData?.sections?.[0]?.fields?.list?.[0]?.fields?.reference?.fields?.url;

    return {
      text: text,
      url: url,
      title: title,
    };
  };

  const getContent = () => {
    if (!data) return {};

    const successSection = data?.sections[0]?.fields;
    const errorSection = data?.sections[1]?.fields;
    const buttonText = data?.sections[2]?.fields;

    return {
      success: {
        title: successSection?.title,
        text: successSection?.description?.content?.[0]?.content?.[0]?.value,
      },
      error: {
        title: errorSection?.title,
        text: errorSection?.description?.content?.[0]?.content?.[0]?.value,
        cta: buttonText?.description?.content?.[0]?.content?.[0]?.value,
      },
    };
  };

  const toggleShowMore = () => {
    setShowMore(showMore => !showMore);
  };

  const handleVerifyEmail = async () => {
    navigate(`/${webPaths.verifyEmail}`);
  };

  const navigateToDashboard = () => {
    navigate(`/${webPaths.dashboard}`);
  };

  const navigateToHome = () => {
    navigate(`/`);
  };

  const localisedMessage = (id: string) => {
    const F: any = FormattedMessage;
    return <F id={id} />;
  };

  return (
    <>
      <Helmet />
      {fetchingContent || fetchingShareData ? (
        <Box
          sx={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {!isError && (
            <Container
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1,
                px: 2.5,
                position: 'fixed',
                top: 0,
                left: '50%',
                transform: 'translate(-50%)',
              }}
            >
              <FullLogo
                onClick={navigateToHome}
                sx={{
                  width: 'auto',
                  height: { xs: '36px', md: '40px' },
                  cursor: 'pointer',
                }}
              />
              <Box
                sx={{
                  cursor: 'pointer',
                  width: { xs: '32px', md: '48px' },
                  height: { xs: '32px', md: '48px' },
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  aspectRatio: '1 / 1',
                  borderRadius: '100%',
                  background: globalColours['$global-colour-background-neutral-default'],
                }}
                data-testid='navigation-close'
                onClick={navigateToDashboard}
              >
                <Icons.Cancel sx={{ fontSize: { xs: '16px', md: '24px' } }} />
              </Box>
            </Container>
          )}
          <Box
            className={className}
            sx={{
              padding: { xs: 3, md: 5 },
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Stack
              sx={{
                gap: 2,
                justifyContent: 'center',
                alignItems: 'center',
                maxWidth: '500px',
                height: '100%',
                mx: 'auto',
              }}
            >
              {!isError ? (
                <>
                  <Icons.GridIcon
                    sx={{
                      mb: 4,
                      width: '96px',
                      height: '96px',
                      color: globalColours['$global-colour-palette-gray-200'],
                    }}
                  />
                  <Typography
                    variant={screens.isDesktop ? 'h3' : 'h4'}
                    data-testid='confirm-email-header'
                    sx={{ textAlign: 'center' }}
                  >
                    {getContent().success.title}
                  </Typography>
                  <Typography variant='body3' sx={{ width: '100%', textAlign: 'center' }}>
                    {getContent().success.text}
                  </Typography>

                  <Stack sx={{ gap: 2, width: '95%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <CheckCircle
                        fontSize='large'
                        sx={{
                          width: '24px',
                          height: '24px',
                          color: globalColours['$global-colour-sentiment-positive-active'],
                        }}
                      />
                      <Typography variant='body2'>
                        {localisedMessage('verify.email.personal.information')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <CheckCircle
                        fontSize='large'
                        sx={{
                          width: '24px',
                          height: '24px',
                          color: globalColours['$global-colour-sentiment-positive-active'],
                        }}
                      />
                      <Typography variant='body2'>
                        {relatedUsers?.length > 1
                          ? `${relatedUsers?.length} children`
                          : `${relatedUsers?.length} child`}{' '}
                        registered
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <CheckCircle
                        fontSize='large'
                        sx={{
                          width: '24px',
                          height: '24px',
                          color: globalColours['$global-colour-sentiment-positive-active'],
                        }}
                      />
                      <Typography variant='body2SemiBold'>
                        {localisedMessage('verify.email.email.verification')}{' '}
                        {user?.email && `- ${user?.email}`}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, width: '100%' }}
                  >
                    <Button
                      onClick={navigateToDashboard}
                      size='large'
                      sx={{ width: '100% !important', border: 'solid 1px transparent' }}
                    >
                      <Typography variant='body3SemiBold' sx={{ textTransform: 'uppercase' }}>
                        {localisedMessage('done')}
                      </Typography>
                    </Button>

                    <RWebShare data={extractData()}>
                      <Button
                        data-event='call_to_action_click'
                        data-traffic-type={
                          process.env.NODE_ENV === 'production' ? 'production' : 'local'
                        }
                        variant='outlined'
                        size='large'
                        onClick={() => {}}
                        sx={{ width: { xs: 'max-content', md: '100%' }, mx: 'auto' }}
                        disableBottomFixed
                      >
                        <Typography variant='body3SemiBold' sx={{ textTransform: 'uppercase' }}>
                          {localisedMessage('refer.friend')}
                        </Typography>
                      </Button>
                    </RWebShare>
                  </Box>
                </>
              ) : (
                <>
                  <Icons.GridIcon
                    sx={{
                      mb: 4,
                      width: '96px',
                      height: '96px',
                      color: globalColours['$global-colour-palette-gray-200'],
                    }}
                  />
                  <Typography
                    variant={screens.isDesktop ? 'h3' : 'h4'}
                    data-testid='confirm-email-header'
                    sx={{ textAlign: 'center' }}
                  >
                    {getContent().error.title}
                  </Typography>
                  <Typography variant='body3' sx={{ width: '100%', textAlign: 'center' }}>
                    {getContent().error.text}
                  </Typography>
                  <Button
                    onClick={handleVerifyEmail}
                    size='large'
                    sx={{ width: '100% !important', border: 'solid 1px transparent' }}
                  >
                    <Typography variant='body3SemiBold' sx={{ textTransform: 'uppercase' }}>
                      {localisedMessage('verify.email')}
                    </Typography>
                  </Button>
                  <Typography
                    variant='caption1'
                    sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                    onClick={toggleShowMore}
                  >
                    {localisedMessage('verify.email.no.email.verification')}{' '}
                    <ArrowDownIcon sx={{ width: '16px', height: '16px' }} />
                  </Typography>
                  {showMore && (
                    <ul style={{ width: '100%', padding: '0 20px', margin: 0 }}>
                      <li>
                        <Typography variant='body2'>
                          {localisedMessage('verify.email.check.spam')}
                        </Typography>
                      </li>
                    </ul>
                  )}
                </>
              )}
            </Stack>
          </Box>
        </>
      )}
    </>
  );
};

EmailConfirmation.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

EmailConfirmation.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(EmailConfirmation)(
  ({ theme }) => `
  &.MuiBox-root {
    background-color: ${theme.palette.background.paper};
    min-height: 100vh;
  }
`,
);
